/**
 * Template de prise de besoin : Politique de gestion des incidents v1
 * Aligné sur ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0
 * Document maître de gouvernance pour la gestion des incidents TI
 */

import type { IntakeTemplate } from '../templateTypes';

export const templatePolitiqueGestionIncidentsV1: IntakeTemplate = {
  id: 'politique-gestion-incidents-v1',
  name: 'Prise de besoin Politique de gestion des incidents v1',
  description:
    'Questionnaire complet pour élaborer une politique de gestion des incidents TI conforme ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0. Document maître définissant les objectifs, portée, gouvernance, enjeux et responsabilités.',
  documentTypeCode: 'INCIDENT_RESPONSE_POLICY',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Contexte & Objectifs',
      description: 'Informations sur l\'entreprise et objectifs de la politique',
      icon: 'Building2',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          required: true,
          helpText: 'Identifiant principal pour la politique de gestion des incidents.',
        },
        {
          id: 'secteur_activite',
          label: 'Secteur d\'activité',
          type: 'text',
          placeholder: 'Ex: Manufacturier, Services financiers, Santé, Éducation...',
          helpText: 'Le secteur d\'activité influence les exigences réglementaires et les risques spécifiques.',
        },
        {
          id: 'objectifs_politique',
          label: 'Quels sont les objectifs principaux de cette politique de gestion des incidents ?',
          type: 'multiselect',
          options: [
            'Réduire le temps de résolution des incidents',
            'Améliorer la communication lors d\'incidents',
            'Assurer la continuité des services',
            'Protéger les données et la confidentialité',
            'Respecter les obligations réglementaires',
            'Améliorer la satisfaction client',
            'Réduire les impacts business',
            'Autre',
          ],
          required: true,
          helpText: 'ITIL 4: Définition claire des objectifs pour guider la mise en œuvre de la politique.',
        },
        {
          id: 'portee_politique',
          label: 'Quelle est la portée de cette politique ?',
          type: 'multiselect',
          options: [
            'Tous les incidents TI',
            'Incidents de sécurité uniquement',
            'Incidents critiques uniquement',
            'Incidents affectant les clients',
            'Incidents internes uniquement',
            'Autre',
          ],
          required: true,
          helpText: 'ISO 27001 (A.16.1): Définition claire de la portée pour assurer une application cohérente.',
        },
        {
          id: 'services_critiques',
          label: 'Quels sont les services TI critiques de votre organisation ?',
          type: 'textarea',
          placeholder: 'Listez les services critiques (ex: Système ERP, Email, Réseau, Serveurs de fichiers, Applications métier...)',
          helpText: 'ITIL 4: Identification des services critiques pour prioriser la gestion des incidents.',
        },
        {
          id: 'ton_document_politique',
          label: 'Quel ton souhaitez-vous pour cette politique ?',
          type: 'select',
          options: [
            'Formel et juridique (ton impératif, sanctions claires)',
            'Pédagogique et collaboratif (ton explicatif, guide pratique)',
            'Mixte (équilibre entre formel et pédagogique)',
          ],
          helpText: 'Le ton influence la réception et l\'adhésion des parties prenantes.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Gouvernance & Responsabilités',
      description: 'Structure de gouvernance et attribution des responsabilités',
      icon: 'Users',
      questions: [
        {
          id: 'structure_gouvernance',
          label: 'Quelle structure de gouvernance souhaitez-vous pour la gestion des incidents ?',
          type: 'select',
          options: [
            'Comité de gestion des incidents dédié',
            'Intégration dans le comité TI existant',
            'Responsable incidents désigné',
            'Gouvernance distribuée par département',
            'À déterminer',
          ],
          helpText: 'COBIT 2019 (APO12): Structure de gouvernance claire essentielle pour la gestion efficace des incidents.',
        },
        {
          id: 'responsable_gestion_incidents',
          label: 'Qui sera responsable de la gestion des incidents ?',
          type: 'text',
          placeholder: 'Ex: Directeur TI, Responsable Cybersécurité, Gestionnaire de service...',
          helpText: 'ITIL 4: Attribution claire du responsable pour imputabilité et coordination.',
        },
        {
          id: 'equipe_gestion_incidents',
          label: 'Quelle équipe sera responsable de la gestion opérationnelle des incidents ?',
          type: 'text',
          placeholder: 'Ex: Équipe support TI, Équipe sécurité, Équipe dédiée incidents...',
          helpText: 'ITIL 4 (Incident Management): Équipe opérationnelle pour la résolution des incidents.',
        },
        {
          id: 'roles_responsabilites',
          label: 'Quels rôles et responsabilités doivent être définis ?',
          type: 'multiselect',
          options: [
            'Gestionnaire d\'incidents',
            'Techniciens de première ligne',
            'Techniciens de deuxième ligne',
            'Techniciens de troisième ligne / Experts',
            'Gestionnaire de service',
            'Responsable sécurité',
            'Direction',
            'Communication / Relations publiques',
            'Autre',
          ],
          helpText: 'ITIL 4: Définition claire des rôles pour éviter les conflits et assurer la coordination.',
        },
        {
          id: 'imputabilite_incidents',
          label: 'Qui a la plus haute autorité et imputabilité en cas d\'incident majeur ?',
          type: 'text',
          placeholder: 'Ex: Directeur général, Directeur TI, Président...',
          helpText: 'COBIT 2019 (APO12.05): Attribution de l\'imputabilité ultime pour les incidents critiques.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Enjeux & Risques',
      description: 'Enjeux business et risques associés aux incidents',
      icon: 'AlertTriangle',
      questions: [
        {
          id: 'enjeux_business',
          label: 'Quels sont les principaux enjeux business liés aux incidents TI ?',
          type: 'multiselect',
          options: [
            'Interruption de service client',
            'Perte de revenus',
            'Dommage à la réputation',
            'Non-conformité réglementaire',
            'Perte de données',
            'Violation de sécurité',
            'Impact sur la productivité',
            'Autre',
          ],
          helpText: 'COBIT 2019 (APO12.05): Identification des enjeux pour prioriser les efforts de gestion.',
        },
        {
          id: 'risques_identifies',
          label: 'Quels risques spécifiques avez-vous identifiés liés à la gestion des incidents ?',
          type: 'multiselect',
          options: [
            'Délai de résolution trop long',
            'Communication inadéquate',
            'Manque de préparation',
            'Absence de procédures claires',
            'Manque de ressources',
            'Manque de formation',
            'Absence de documentation',
            'Autre',
          ],
          helpText: 'ISO 27001 (A.16.1): Identification des risques pour établir des mesures de mitigation.',
        },
        {
          id: 'impact_financier',
          label: 'Quel est l\'impact financier estimé d\'un incident majeur non géré ?',
          type: 'select',
          options: [
            'Moins de 10 000$',
            '10 000$ - 50 000$',
            '50 000$ - 100 000$',
            '100 000$ - 500 000$',
            'Plus de 500 000$',
            'À déterminer',
          ],
          helpText: 'Évaluation de l\'impact financier pour justifier les investissements en gestion d\'incidents.',
        },
        {
          id: 'obligations_reglementaires',
          label: 'Quelles obligations réglementaires doivent être respectées ?',
          type: 'multiselect',
          options: [
            'Loi 25 (protection des renseignements personnels)',
            'RGPD',
            'PCI-DSS',
            'HIPAA',
            'Normes sectorielles spécifiques',
            'Aucune obligation spécifique',
            'Autre',
          ],
          helpText: 'ISO 27001 (A.18.1): Conformité réglementaire essentielle pour éviter les sanctions.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Processus & Procédures',
      description: 'Processus de gestion des incidents et procédures associées',
      icon: 'FileCheck',
      questions: [
        {
          id: 'processus_gestion_incidents',
          label: 'Quel processus de gestion des incidents souhaitez-vous mettre en place ?',
          type: 'select',
          options: [
            'Processus ITIL 4 complet',
            'Processus simplifié adapté',
            'Processus personnalisé',
            'À déterminer',
          ],
          helpText: 'ITIL 4: Processus structuré pour assurer une gestion efficace et cohérente des incidents.',
        },
        {
          id: 'outils_gestion_incidents',
          label: 'Quels outils sont utilisés ou prévus pour la gestion des incidents ?',
          type: 'multiselect',
          options: [
            'Système de ticketing (Jira, ServiceNow, etc.)',
            'Outil de monitoring',
            'Outil de communication (Teams, Slack)',
            'Outil de documentation',
            'Aucun outil spécifique',
            'À déterminer',
          ],
          helpText: 'ITIL 4: Outils appropriés essentiels pour supporter le processus de gestion des incidents.',
        },
        {
          id: 'communication_incidents',
          label: 'Comment souhaitez-vous gérer la communication lors d\'incidents ?',
          type: 'multiselect',
          options: [
            'Communication interne uniquement',
            'Communication clients si impact',
            'Communication publique si nécessaire',
            'Communication réglementaire (Loi 25, etc.)',
            'Autre',
          ],
          helpText: 'NIST CSF 2.0 (RS.CO): Communication claire essentielle pour gérer les attentes et maintenir la confiance.',
        },
        {
          id: 'formation_equipe',
          label: 'Souhaitez-vous rendre la formation en gestion d\'incidents obligatoire ?',
          type: 'select',
          options: ['Oui, pour tous', 'Oui, pour l\'équipe TI uniquement', 'Non, formation recommandée', 'À déterminer'],
          helpText: 'ISO 27001 (A.7.2.2): Formation essentielle pour assurer la compétence de l\'équipe.',
        },
        {
          id: 'revision_politique',
          label: 'À quelle fréquence souhaitez-vous réviser cette politique ?',
          type: 'select',
          options: ['Annuelle', 'Semestrielle', 'Trimestrielle', 'Lors de changements majeurs', 'À déterminer'],
          helpText: 'ITIL 4: Révision régulière essentielle pour maintenir la pertinence de la politique.',
        },
      ],
    },
  ],
};





